// The small inline SVGs the list editors and upload slots draw their controls
// with. They live together so a glyph is defined once and reads identically
// wherever it appears — the reorder arrows in the kit-contents list are the same
// mark as the ones in the build-step list, and one remove "×" serves every list
// row and photo tile.
//
// Every icon is `aria-hidden`: its button already carries a translated
// `aria-label`, so the glyph must never become the accessible name. They inherit
// `currentColor` and size in `em`, so a button's own colour and font size drive
// them and a disabled or danger variant needs no icon-side change.

type IconProps = {
  className?: string;
};

function Svg({
  className,
  children,
}: IconProps & { children: React.ReactNode }): JSX.Element {
  return (
    <svg
      className={className ? `control-icon ${className}` : 'control-icon'}
      viewBox="0 0 16 16"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export function MoveUpIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M8 13V3.5" />
      <path d="M3.75 7.75 8 3.5l4.25 4.25" />
    </Svg>
  );
}

export function MoveDownIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M8 3v9.5" />
      <path d="M12.25 8.25 8 12.5 3.75 8.25" />
    </Svg>
  );
}

export function RemoveIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 4l8 8" />
      <path d="M12 4l-8 8" />
    </Svg>
  );
}

// The pencil on a folded row's "Edit" cue, drawn at the same weight as the
// reorder and remove marks beside it so the row's controls read as one set.
export function EditIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M10.75 2.75l2.5 2.5L6 12.5l-3.25.75.75-3.25z" />
      <path d="M9.25 4.25l2.5 2.5" />
    </Svg>
  );
}

// The disclosure chevron on a picker's trigger button (SearchableSelect). Points
// down closed and is rotated by CSS when open, so the open and closed states are
// one glyph rather than two that could drift apart.
export function ChevronDownIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 6.25 8 10.25l4-4" />
    </Svg>
  );
}
